import {
  PURCHASE_ORDER_SOURCE_LABELS,
  PURCHASE_ORDER_STATUS_LABELS,
  type PurchaseOrder,
  type PurchaseOrderSource,
  type PurchaseOrderStatus,
} from "@xop-any-time/shared";
import { Chip } from "@mui/material";
import type { JSX } from "react";

/**
 * The purchase-order furniture the list, the document page and the
 * quote-accepted dialog share, so an order cannot be described one way in the
 * table and another on its own page. Modelled on `quotes/quote-ui.tsx`.
 */

/**
 * `received` is the state XOP still has to act on, so it is the accenting
 * one; `accepted` is done; `cancelled` is neutral — not red, because a
 * cancelled order is a decision, not a failure.
 */
const CHIP_COLOR: Record<PurchaseOrderStatus, "info" | "success" | "default"> = {
  received: "info",
  accepted: "success",
  cancelled: "default",
};

export function PurchaseOrderStatusChip({ status }: { status: PurchaseOrderStatus }): JSX.Element {
  return (
    <Chip
      size="small"
      data-testid="po-status"
      variant={status === "cancelled" ? "outlined" : "filled"}
      color={CHIP_COLOR[status]}
      label={PURCHASE_ORDER_STATUS_LABELS[status]}
      sx={{ height: 20, fontSize: 11.5, fontWeight: 600 }}
    />
  );
}

export function PurchaseOrderSourceChip({ source }: { source: PurchaseOrderSource }): JSX.Element {
  return (
    <Chip
      size="small"
      variant="outlined"
      data-testid="po-source"
      label={PURCHASE_ORDER_SOURCE_LABELS[source]}
      sx={{ height: 20, fontSize: 11.5, fontWeight: 500 }}
    />
  );
}

/** "PO-2026-0007", or "#12" for the (bug-shaped) row crm-api sent without one. */
export function purchaseOrderTitle(po: Pick<PurchaseOrder, "id" | "number">): string {
  return po.number ?? `#${po.id}`;
}

/** What a purchase order IS, said once and used by the empty list and the
 * new-order page alike. */
export const PURCHASE_ORDER_EXPLAINER =
  "A purchase order is your order to XOP Networks. Make one from a quote you have accepted — open the quote and press Create purchase order — or upload your own purchase order document with Add purchase order.";
